import { cosmicDateRangePickerValueSchema, EmptyStateProps } from '@taxbit-private/cosmic'
import { z } from 'zod'

import { filterAssetSchema } from 'pages/dashboard/helpers/dashboardFiltersTypes'

import { ReportType } from 'api/types/report/reportApiTypes'

export enum ReportCategory {
  General = 'general',
  EntriesAndAdjustments = 'entriesAndAdjustments',
  Disclosure = 'disclosure',
  Qa = 'qa',
  ReferenceData = 'referenceData',
  System = 'system',
}

export enum ReportTab {
  Accounting = 'Accounting',
  Tax = 'Tax',
}

export type ReportTable = {
  name: string
  description: string
  isFavorite?: boolean
  key: ReportType
  reportCategory?: ReportCategory
}

export type ListReportTable = {
  [ReportCategory.General]: ReportTable[]
  [ReportCategory.EntriesAndAdjustments]: ReportTable[]
  [ReportCategory.Disclosure]: ReportTable[]
  [ReportCategory.Qa]: ReportTable[]
  [ReportCategory.ReferenceData]: ReportTable[]
  [ReportCategory.System]: ReportTable[]
}

export const reportFilterFormSchema = z.object({
  dateRange: cosmicDateRangePickerValueSchema.catch({}),
  sources: z.string().catch(''),
  assets: z.array(filterAssetSchema).catch([]),
  sourceTags: z.array(z.string()).catch([]),
  multipleSources: z.array(z.string()).default([]),
  version: z.string().optional(),
  transactionId: z.string().catch(''),
})

export type ReportFilterFormFields = z.infer<typeof reportFilterFormSchema>

export const reportTitles = {
  [ReportType.SignificantHoldings]: 'Significant Holdings',
  [ReportType.SignificantHoldingsDetailed]: 'Significant Holdings - Detailed',
  [ReportType.GainLoss]: 'Realized Gain/Loss - Detailed',
  [ReportType.QaAsset]: 'Inventory Report - Summary',
  [ReportType.Asset]: 'Inventory Report - Summary',
  [ReportType.Balance]: 'Balance Report - Detailed',
  [ReportType.Nft]: 'NFT Report',
  [ReportType.ImpairmentSummary]: 'Impairment - Summary',
  [ReportType.ImpairmentLotLevel]: 'Impairment - Lot Level',
  [ReportType.FairMarketValueSummary]: 'Fair Value Summary',
  [ReportType.FairMarketValueLotLevel]: 'Fair Value Lot Level',
  [ReportType.ReconciliationRollForward]: 'ASC 350-60 Roll Forward',
  [ReportType.PricesDailySummary]: 'Daily Prices',
  [ReportType.JournalEntryDetailed]: 'Journal Entry Report - Detailed',
  [ReportType.JournalEntrySummary]: 'Journal Entry Report - Summary',
  [ReportType.RollingGainLoss]: 'Realized Gain/Loss - Summary',
  [ReportType.GainlossSummary]: 'Realized Gain/Loss - Summary',
  [ReportType.QaRollForward]: 'Roll Forward',
  [ReportType.RollForward]: 'Legacy Roll Forward (In Progress)',
  [ReportType.QaFairMarketValueSummary]: 'Fair Value Summary',
  [ReportType.FairMarketValueLotDispositions]: 'Fair Value Adjustment Detail - Disposition',
  [ReportType.FairMarketValueLotEndingInventory]:
    'Fair Value Adjustment Detail - Ending Inventory by Lot',
  [ReportType.QaBalance]: 'Balance Report - Detailed',
  [ReportType.QaSignificantHoldings]: 'Significant Holdings',
  [ReportType.Asc35060]: 'ASC 350-60 Roll Forward',
  [ReportType.QaGlobalEntityHoldings]: 'Global Entity Holdings',
  [ReportType.QaGlobalAssetHoldings]: 'Global Asset Holdings',
  [ReportType.QaJournalEntryDetailed]: 'Journal Entry Report - Detailed',
  [ReportType.TransactionEditHistory]: 'Transaction Edit History',
  [ReportType.ImpairmentLotEndingInventory]: 'Impairment Detail - Ending Inventory by Lot',
  [ReportType.ImpairmentDisposition]: 'Impairment Detail - Disposition',
  [ReportType.QaImpairmentSummary]: 'Impairment Summary',
}

export const reportCategoryTitles = {
  [ReportCategory.General]: 'General',
  [ReportCategory.EntriesAndAdjustments]: 'Entries & Adjustments',
  [ReportCategory.Disclosure]: 'Disclosures',
  [ReportCategory.Qa]: 'QA',
  [ReportCategory.ReferenceData]: 'Reference Data',
  [ReportCategory.System]: 'System Reports',
}

export const reportParamsSchema = z.object({
  reportType: z.nativeEnum(ReportCategory),
  reportId: z.string(),
})

export type ReportParams = z.infer<typeof reportParamsSchema>

export const DEFAULT_REPORT_EMPTY_STATE_PROPS: EmptyStateProps = {
  title: 'There is no activity available for the current filters.',
  subtitleElement: 'Please adjust your filters and try again.',
}

export type AssetTableAmountKey = `${string} Amount`
type AssetTableValueKey = `${string} Value`
type AssetTableCostBasisKey = `${string} Cost Basis`

export type AssetTableRowKey = AssetTableAmountKey | AssetTableValueKey | AssetTableCostBasisKey

export type AssetTableRow = {
  date: string
} & Record<AssetTableRowKey, string | undefined>

export enum ReportSupportedFilters {
  DateRange = 'dateRange',
  Sources = 'sources',
  Assets = 'assets',
  SourceTags = 'sourceTags',
  MultipleSources = 'multipleSources',
  Version = 'version',
  TransactionId = 'transactionId',
}

export type PeriodOption = {
  label: string
  value: string
  startDate: Date
  endDate: Date
}

export type SourceOption = {
  label: string
  value: string // Either: 1. JSON stringified Inventory object, 2. '@' string (legacy way)
  optionDescription: string
}
